import React, { type PropsWithChildren, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { get, upperCase } from 'lodash';
import queryString from 'query-string';

import { LocalizationManager } from './LocalizationManager';
import { languages } from '.';
import { Provider } from './LocalizationContext';
import { LOCALES, getShortLanguageCode } from '../utils/locale';
import { history } from '../store/history';
import { changeLanguage } from '../store/actions/settings';
import { type RootState } from '../store/types';

const mapStateToProps = (state: RootState) => {
    const { locale } = state.settings;
    return { locale };
};

const QUERY_PARAM = 'lang';
type LocalizationNode = string | { [key: string]: LocalizationNode };
type LanguagePhrases = Record<string, LocalizationNode>;

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

type LocalizationProviderBaseProps = PropsWithChildren<PropsFromRedux>;

const LocalizationProviderBase = (props: LocalizationProviderBaseProps) => {
    const dispatch = useDispatch();
    const { locale, children } = props;

    const queryParams = queryString.parse(history.location.search);
    const langQueryParam = get(queryParams, QUERY_PARAM, upperCase(getShortLanguageCode(locale))) as string;
    const localizationLanguage = (LOCALES as Record<string, string>)[langQueryParam];

    useEffect(() => {
        dispatch(changeLanguage(localizationLanguage));
    }, [dispatch, localizationLanguage]);

    const localization = useMemo(
        () =>
            new LocalizationManager({
                currentLocale: locale,
                phrases: ((languages as unknown as Record<string, LanguagePhrases>)[getShortLanguageCode(locale)] ??
                    {}) as Record<string, string | Record<string, string>>,
                onMissingKey: (key: string, options: Record<string, string>) => {
                    const defaultValue = options.defaultValue;
                    if (defaultValue) {
                        return defaultValue;
                    }
                    console.warn(`Missing translation for key: "${key}"`);
                    return key;
                }
            }),
        [locale]
    );

    return <Provider value={localization}>{children}</Provider>;
};

export const LocalizationProvider = connect(mapStateToProps)(LocalizationProviderBase);

LocalizationProvider.displayName = 'LocalizationProvider';
